import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";

const nav = [
  ["/services", "Flooring"],
  ["/gallery", "Our work"],
  ["/about", "About"],
  ["/reviews", "Reviews"],
  ["/blog", "Advice"],
  ["/contact", "Visit us"],
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerCompact, setHeaderCompact] = useState(false);
  const scrollFrame = useRef<number | null>(null);
  const location = useLocation();

  useLayoutEffect(() => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    const frame = window.requestAnimationFrame(() => {
      setMenuOpen(false);
      setHeaderCompact(false);
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      if (scrollFrame.current !== null) return;
      scrollFrame.current = window.requestAnimationFrame(() => {
        setHeaderCompact(window.scrollY > 28);
        scrollFrame.current = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollFrame.current !== null) window.cancelAnimationFrame(scrollFrame.current);
    };
  }, []);

  if (location.pathname.startsWith("/admin")) {
    return (
      <>
        <a className="skip-link" href="#main">Skip to admin content</a>
        <main id="main">{children}</main>
      </>
    );
  }

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <header className={`site-header ${headerCompact ? "is-compact" : ""}`}>
        <div className="scroll-progress" aria-hidden="true" />
        <div className="shell header-inner">
          <Link to="/" className="brand-link" aria-label="Horncastle Flooring home">
            <img className="brand-full" src="/images/brand/logo.svg" alt="Horncastle Flooring" />
            <img className="brand-mark" src="/images/brand/mark.svg" alt="" />
          </Link>
          <nav className="desktop-nav" aria-label="Main navigation">
            {nav.map(([href, label]) => (
              <NavLink key={href} to={href} className={({ isActive }) => isActive ? "active" : ""}>{label}</NavLink>
            ))}
          </nav>
          <div className="header-actions">
            <a className="header-phone" href="tel:+441507237890"><small>Call showroom</small><strong>01507 237890</strong></a>
            <Link className="button button-small" to="/get-a-quote">Free measure <span aria-hidden="true">↘</span></Link>
            <button className="icon-button menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-controls="mobile-menu" aria-label="Toggle menu">
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        <nav id="mobile-menu" className={`mobile-nav ${menuOpen ? "open" : ""}`} aria-label="Mobile navigation">
          <p>Explore Horncastle Flooring</p>
          {nav.map(([href, label], index) => <Link key={href} to={href}><span>0{index + 1}</span>{label}</Link>)}
          <div><Link to="/track">Track my job</Link><Link to="/get-a-quote">Book a free measure</Link></div>
        </nav>
      </header>
      <main id="main">{children}</main>
      <Footer />
      <a
        className="whatsapp-float"
        href="https://wa.me/447592600333?text=Hi%20Horncastle%20Flooring%2C%20I%27d%20like%20a%20free%20measure%20and%20quote."
        target="_blank"
        rel="noopener"
        aria-label="Chat with Horncastle Flooring on WhatsApp"
      >
        <span className="whatsapp-icon"><WhatsAppIcon /></span><span className="whatsapp-label">Chat to us</span>
      </a>
      <div className="mobile-cta" aria-label="Quick contact">
        <a href="tel:+441507237890"><Phone />Call</a>
        <a href="https://wa.me/447592600333" aria-label="Chat on WhatsApp"><WhatsAppIcon />WhatsApp</a>
        <Link to="/get-a-quote">Free quote</Link>
      </div>
      <CookieBanner />
    </>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M16 3.25A12.5 12.5 0 0 0 5.4 22.37L3.72 28.5l6.27-1.64A12.5 12.5 0 1 0 16 3.25Zm0 22.74c-2.03 0-4-.6-5.68-1.73l-.4-.24-3.72.98 1-3.62-.26-.42A10.23 10.23 0 1 1 16 26Z" />
      <path fill="currentColor" d="M21.72 18.36c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47a8.9 8.9 0 0 1-1.65-2.05c-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.08-.8.38-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.23 1.35.2 1.86.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.3.18-1.42-.08-.13-.28-.2-.57-.35Z" />
    </svg>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="shell footer-grid">
        <div className="footer-brand">
          <img src="/images/brand/logo.svg" alt="Horncastle Flooring" />
          <p>Independent flooring advice, careful fitting and straight answers from a family-run Horncastle retailer.</p>
          <a href="tel:+441507237890">01507 237890</a>
          <a href="mailto:horncastleflooringretailer@mail.com">horncastleflooringretailer@mail.com</a>
        </div>
        <div>
          <h2>Explore</h2>
          <Link to="/services">Flooring</Link><Link to="/gallery">Our work</Link><Link to="/reviews">Reviews</Link><Link to="/blog">Advice</Link>
        </div>
        <div>
          <h2>Plan your visit</h2>
          <Link to="/get-a-quote">Free measure & quote</Link><Link to="/track">Track my job</Link><Link to="/contact">Contact & directions</Link><Link to="/faq">Questions answered</Link>
        </div>
        <div>
          <h2>Showroom</h2>
          <address>Eggbert Court<br />Wharf Road<br />Horncastle, LN9 5FQ</address>
          <p>Mon–Fri 9:00–17:00<br />Sat 9:00–13:00<br /><small>Hours to be confirmed</small></p>
        </div>
      </div>
      <div className="shell footer-bottom">
        <div className="legal-links"><Link to="/privacy-policy">Privacy</Link><Link to="/cookie-policy">Cookies</Link><Link to="/terms-conditions">Terms</Link><Link to="/refund-policy">Refunds</Link><Link to="/admin">Admin</Link></div>
        <a href="https://instagram.com/buildwithduke" target="_blank" rel="noopener">Website design by buildwithduke</a>
      </div>
    </footer>
  );
}

function CookieBanner() {
  const stored = typeof window !== "undefined" ? localStorage.getItem("hf-cookie-choice") : null;
  const { pathname } = useLocation();
  if (stored || pathname === "/cookie-policy") return null;
  const choose = (value: string) => {
    localStorage.setItem("hf-cookie-choice", value);
    window.dispatchEvent(new Event("storage"));
    window.location.reload();
  };
  return (
    <aside className="cookie-banner" aria-label="Cookie choices">
      <div>
        <strong>Your privacy, your choice</strong>
        <p>We use necessary cookies to make the site work. Optional analytics only run if you accept them.</p>
      </div>
      <div className="cookie-actions">
        <button className="button button-ghost" onClick={() => choose("necessary")}>Necessary only</button>
        <button className="button" onClick={() => choose("all")}>Accept all</button>
      </div>
    </aside>
  );
}
